import * as LDClient from 'launchdarkly-js-client-sdk';
import { EventConsts, ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { isEmpty } from 'lodash';
import ConfigService from 'client/util/ConfigService';
import LocalStorageService from 'client/util/LocalStorageService';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import Vue from 'vue';
import shajs from 'sha.js';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

/**
 * These are the names of all feature flags that are available to the application. They are kept
 * here, in the FeatureFlagsModule so that they can be imported and used instead of repeating
 * magic strings throughout the application when feature flags are needed.
 */
export const featureFlags = {
  androidEmmMvp: 'androidEmmMvp',
  applicationWorkflow: 'applicationWorkflow',
  authNPolicyFilters: 'authNPolicyFilters',
  automaticAnnualSelfServe: 'automaticAnnualSelfServe',
  browserPatchReport: 'browserPatchReport',
  bambooRealTimeImport: 'bambooRealTimeImport',
  cloudInsights: 'cloudInsights',
  cloudInsightsToolbarV2: 'cloudInsightsToolbarV2',
  cloudInsightsTrial: 'cloudInsightsTrial',
  commandTemplateManage: 'commandTemplateManage',
  commandTemplateEdit: 'commandTemplateEdit',
  commandTriggerRegistration: 'commandTriggerRegistration',
  commandTriggerLogin: 'commandTriggerLogin',
  conditionalAccessAsideUpdate: 'conditionalAccessAsideUpdate',
  customApiScheduledImports: 'customApiScheduledImports',
  deviceAsAGateway: 'deviceAsAGateway',
  deviceAsideHighlightsTab: 'deviceAsideHighlightsTab',
  deviceCertBasedRadius: 'deviceCertBasedRadius',
  deviceOverviewEndOfService: 'deviceOverviewEndOfService',
  diEventsSearch: 'diEventsSearch',
  dynamicDeviceGroupsUI: 'dynamicDeviceGroupsUI',
  exportManagerToGoogle: 'exportManagerToGoogle',
  fairUseChanges: 'fairUseChanges',
  homePage: 'homePage',
  homePageV2Blue: 'homePageV2Blue',
  homePageV2Orange: 'homePageV2Orange',
  homePageWidgetsPhase2: 'homePageWidgetsPhase2',
  implementationGuides: 'implementationGuides',
  importAdditionalAttributesFromM365: 'importAdditionalAttributesFromM365',
  importManagerAttributeFromGoogle: 'importManagerAttributeFromGoogle',
  importUserUpdatesCustomApi: 'importUserUpdatesCustomApi',
  intercomSupport: 'intercomSupport',
  ironSquidOsUpdates: 'ironSquidOsUpdates',
  labsDeviceGroupVPN: 'labsDeviceGroupVPN',
  ldapCustomAttributes: 'ldapCustomAttributes',
  membershipSuggestionAutomation: 'membershipSuggestionAutomation',
  navBarSsoExperiment: 'navBarSsoExperiment',
  o365GroupExports: 'o365GroupExports',
  osManagementAnnouncement: 'osManagementAnnouncement',
  osPatchMgmtPolicyReport: 'osPatchMgmtPolicyReport',
  passwordManagerGtm: 'passwordManagerGtm',
  passwordManager: 'passwordManager',
  passwordManagerTrial: 'passwordManagerTrial',
  passwordResetViaRecoveryEmail: 'passwordResetViaRecoveryEmail',
  pkiConsole: 'pkiConsole',
  platformAuthenticator: 'platformAuthenticator',
  policyGroupTemplateGallery: 'policyGroupTemplateGallery',
  policyGroupTemplateCreate: 'policyGroupTemplateCreate',
  promptedAssist: 'promptedAssist',
  policyGroupTemplateManage: 'policyGroupTemplateManage',
  publicUrlMetadata: 'publicUrlMetadata',
  purchaseAddOnsInProduct: 'purchaseAddOnsInProduct',
  readOnlyEmailTemplate: 'readOnlyEmailTemplate',
  remoteAssist: 'remoteAssist',
  remoteAssistForLinux: 'remoteAssistForLinux',
  remoteAssistOnboarding: 'remoteAssistOnboarding',
  reportFiltration: 'reportFiltration',
  samlMultiAcs: 'samlMultiAcs',
  scimSupportGong: 'scimSupportGong',
  scimSupportPaylocity: 'scimSupportPaylocity',
  settingsFeatureTrials: 'settingsFeatureTrials',
  ssoCustomSamlSubjectNameId: 'ssoCustomSamlSubjectNameId',
  ssoFilter: 'ssoFilter',
  ssoHiddenApps: 'ssoHiddenApps',
  ssoReport: 'ssoReport',
  ssoShowInUserPortal: 'ssoShowInUserPortal',
  synthesizedReports: 'synthesizedReports',
  trialRecaps: 'trialRecaps',
  upgradeNudge: 'upgradeNudge',
  upgradePage: 'upgradePage',
  usePaginationMethodForBoundDevices: 'usePaginationMethodForBoundDevices',
  userNotifications: 'userNotifications',
  usersToDevicesReport: 'usersToDevicesReport',
  usersToDirectoriesReport: 'usersToDirectoriesReport',
  usersToLdapServersReport: 'usersToLdapServersReport',
  usersToRadiusServersReport: 'usersToRadiusServersReport',
  usersToUserGroupsReport: 'usersToUserGroupsReport',
  valueReport: 'valueReport',
  windowsMdmEnrollment: 'windowsMdmEnrollment',
  zzzFlagForTesting: 'zzzFlagForTesting',
};

/**
 * This hash maps beta feature keys used by components to feature flag names used in the feature
 * management provider. It allows for the overriding of existing Mongo feature flags with
 * LaunchDarkly feature flags and the use of more succinct names for LaunchDarkly feature flags.
 */
export const featureFlagToProviderMap = {
  [featureFlags.androidEmmMvp]: '2022-11-09-android-emm-mvp',
  [featureFlags.applicationWorkflow]: '2023-01-10-application-workflow',
  [featureFlags.authNPolicyFilters]: '2022-03-29-authn-policy-filters',
  [featureFlags.automaticAnnualSelfServe]: '2023-01-12-automatic-annual-self-serve',
  [featureFlags.bambooRealTimeImport]: '2021-04-16-bamboo-real-time-import',
  [featureFlags.browserPatchReport]: '2022-09-14-browser-patch-report',
  [featureFlags.cloudInsights]: '2022-02-25-cloud-insights',
  [featureFlags.cloudInsightsToolbarV2]: '2022-09-09-cloud-insights-toolbar-v2',
  [featureFlags.cloudInsightsTrial]: '2022-10-10-cloud-insights-trial',
  [featureFlags.commandTemplateManage]: '2022-08-26-mtp-manage-command-templates',
  [featureFlags.commandTemplateEdit]: '2022-09-28-edit-command-templates',
  [featureFlags.commandTriggerRegistration]: '2022-09-21-command-triggers',
  [featureFlags.commandTriggerLogin]: '2022-12-01-command-triggers-login',
  [featureFlags.conditionalAccessAsideUpdate]: '2022-10-17-conditional-access-policies-aside-panel-ca-fde',
  [featureFlags.customApiScheduledImports]: '2023-01-30-custom-api-scheduled-imports',
  [featureFlags.deviceAsAGateway]: '2023-03-01-device-as-gateway',
  [featureFlags.deviceAsideHighlightsTab]: '2021-07-14-device-aside-highlights-tab',
  [featureFlags.deviceCertBasedRadius]: '2022-08-16-device-cert-based-radius',
  [featureFlags.deviceOverviewEndOfService]: '2022-12-12-device-overview-end-of-service',
  [featureFlags.diEventsSearch]: '2022-12-19-di-events-search',
  [featureFlags.dynamicDeviceGroupsUI]: '2023-03-15-dynamic-device-groups-ui',
  [featureFlags.exportManagerToGoogle]: '2021-07-02-export-manager-attribute-google-workspace',
  [featureFlags.fairUseChanges]: '2023-01-06-fair-use-changes',
  [featureFlags.homePage]: '2021-01-06-home-page',
  [featureFlags.homePageV2Blue]: '2022-Homepage-v2-Blue',
  [featureFlags.homePageV2Orange]: '2022-Homepage-v2-Orange',
  [featureFlags.homePageWidgetsPhase2]: '2022-06-Homepage-v2-Widgets',
  [featureFlags.implementationGuides]: '2022-02-03-implementation-guides',
  [featureFlags.importAdditionalAttributesFromM365]: '2021-08-09-import-additional-attributes-m365',
  [featureFlags.importManagerAttributeFromGoogle]: '2022-04-19-import-manager-attribute-google',
  [featureFlags.importUserUpdatesCustomApi]: '2022-08-16-import-user-updates-custom-api',
  [featureFlags.intercomSupport]: '2022-7-6-intercom-support',
  [featureFlags.ironSquidOsUpdates]: '2022-10-27-iron-squid',
  [featureFlags.labsDeviceGroupVPN]: '2022-12-08-labs-device-group-vpn',
  [featureFlags.ldapCustomAttributes]: '2021-07-08-ldap-custom-attributes',
  [featureFlags.membershipSuggestionAutomation]: '2022-08-18-membership-suggestion-automation',
  [featureFlags.navBarSsoExperiment]: '2022-11-08-nav-bar-sso-experiment',
  [featureFlags.o365GroupExports]: '2022-03-31-m365-usergroup-directory-exports',
  [featureFlags.osManagementAnnouncement]: '2022-02-22-os-management-announcement',
  [featureFlags.osPatchMgmtPolicyReport]: '2022-06-27-os-patch-management-report',
  [featureFlags.passwordManagerGtm]: '2022-10-18-pwd-manager-gtm',
  [featureFlags.passwordManager]: '2022-05-25-Password-Manager-Toggle',
  [featureFlags.passwordManagerTrial]: '2022-11-22-password-manager-trial',
  [featureFlags.passwordResetViaRecoveryEmail]: '2021-10-14-pwd-reset-via-recovery-email',
  [featureFlags.policyGroupTemplateCreate]: '2022-11-10-create-policy-group-templates',
  [featureFlags.policyGroupTemplateManage]: '2023-01-23-policy-group-templates-management',
  [featureFlags.publicUrlMetadata]: '2023-01-12-iridium-public-url-metadata',
  [featureFlags.purchaseAddOnsInProduct]: '2022-10-27-purchase-addons-in-product',
  [featureFlags.pkiConsole]: '2022-02-03-PKI-console',
  [featureFlags.platformAuthenticator]: '2022-12-06-platform-authenticator',
  [featureFlags.policyGroupTemplateGallery]: '2022-11-15-policy-group-template-gallery',
  [featureFlags.promptedAssist]: '2023-01-31-remote-assist-join-with-consent-prompt',
  [featureFlags.readOnlyEmailTemplate]: '2023-01-24-restrict-email-customization',
  [featureFlags.remoteAssist]: '2022-08-01-remote-assist',
  [featureFlags.remoteAssistForLinux]: '2023-01-27-remote-assist-for-linux',
  [featureFlags.remoteAssistOnboarding]: '2022-10-13-remote-assist-onboarding',
  [featureFlags.reportFiltration]: '2022-02-24-report-filtration',
  [featureFlags.samlMultiAcs]: '2023-02-03-SAML-Multiple-ACS-URLs',
  [featureFlags.scimSupportGong]: '2022-05-30-gong-scim-idm-support',
  [featureFlags.scimSupportPaylocity]: '2023-01-30-paylocity-scim-user-import',
  [featureFlags.settingsFeatureTrials]: '2021-12-16-feature-trials',
  [featureFlags.ssoCustomSamlSubjectNameId]: '2023-01-31-sso-samlsubject-nameid-employeeid-and-custom-attributes',
  [featureFlags.ssoFilter]: '2021-09-08-SSO-Filter',
  [featureFlags.ssoHiddenApps]: '2022-11-10-sso-hidden-apps',
  [featureFlags.ssoReport]: '2022-04-13-sso-report',
  [featureFlags.ssoShowInUserPortal]: '2023-01-20-sso-show-in-user-portal-table',
  [featureFlags.synthesizedReports]: '2021-04-01-synthesized-reports',
  [featureFlags.trialRecaps]: '2022-07-21-trial-recap-flag',
  [featureFlags.upgradeNudge]: '2022-06-15-upgrade-nudge',
  [featureFlags.upgradePage]: '2022-05-06-upgrade-page',
  [featureFlags.usePaginationMethodForBoundDevices]: '2023-01-10-show-bound-devices-traversals',
  [featureFlags.userNotifications]: '2021-10-22-user-notifications',
  [featureFlags.usersToDevicesReport]: '2022-09-14-users-to-devices-report',
  [featureFlags.usersToDirectoriesReport]: '2022-09-14-users-to-directories-report',
  [featureFlags.usersToLdapServersReport]: '2022-09-14-users-to-ldap-servers-report',
  [featureFlags.usersToRadiusServersReport]: '2022-09-14-users-to-radius-servers-report',
  [featureFlags.usersToUserGroupsReport]: '2022-12-20-users-to-user-groups-report',
  [featureFlags.valueReport]: '2022-01-21-value-report',
  [featureFlags.windowsMdmEnrollment]: '2022-09-06-windows-mdm-enrollment',
  [featureFlags.zzzFlagForTesting]: '2075-10-11-flag-for-testing',
};

/**
 * Default values for feature flags will be used when the launchDarklySource is local storage. This
 * is useful in this use case so that flags that have targeting enabled and return a particular
 * default value from LD can return the same default value when working with local storage.
 */
export const featureFlagDefaultValues = {
  [featureFlags.androidEmmMvp]: false,
  [featureFlags.applicationWorkflow]: false,
  [featureFlags.authNPolicyFilters]: false,
  [featureFlags.automaticAnnualSelfServe]: false,
  [featureFlags.bambooRealTimeImport]: false,
  [featureFlags.browserPatchReport]: false,
  [featureFlags.cloudInsights]: false,
  [featureFlags.commandTemplateManage]: true,
  [featureFlags.commandTemplateEdit]: true,
  [featureFlags.commandTriggerRegistration]: false,
  [featureFlags.commandTriggerLogin]: false,
  [featureFlags.conditionalAccessAsideUpdate]: false,
  [featureFlags.deviceAsAGateway]: false,
  [featureFlags.customApiScheduledImports]: true,
  [featureFlags.deviceAsideHighlightsTab]: false,
  [featureFlags.deviceCertBasedRadius]: false,
  [featureFlags.deviceOverviewEndOfService]: false,
  [featureFlags.diEventsSearch]: false,
  [featureFlags.dynamicDeviceGroupsUI]: false,
  [featureFlags.exportManagerToGoogle]: false,
  [featureFlags.fairUseChanges]: false,
  [featureFlags.homePage]: true,
  [featureFlags.implementationGuides]: false,
  [featureFlags.importAdditionalAttributesFromM365]: false,
  [featureFlags.importManagerAttributeFromGoogle]: false,
  [featureFlags.importUserUpdatesCustomApi]: true,
  [featureFlags.intercomSupport]: false,
  [featureFlags.ironSquidOsUpdates]: false,
  [featureFlags.labsDeviceGroupVPN]: false,
  [featureFlags.ldapCustomAttributes]: false,
  [featureFlags.membershipSuggestionAutomation]: false,
  [featureFlags.navBarSsoExperiment]: false,
  [featureFlags.o365GroupExports]: false,
  [featureFlags.osManagementAnnouncement]: true,
  [featureFlags.osPatchMgmtPolicyReport]: false,
  [featureFlags.passwordManagerGtm]: false,
  [featureFlags.passwordManager]: false,
  [featureFlags.passwordManagerTrial]: true,
  [featureFlags.passwordResetViaRecoveryEmail]: false,
  [featureFlags.paywallPopup]: false,
  [featureFlags.policyGroupTemplateCreate]: false,
  [featureFlags.purchaseAddOnsInProduct]: false,
  [featureFlags.pkiConsole]: false,
  [featureFlags.platformAuthenticator]: false,
  [featureFlags.policyGroupTemplateGallery]: false,
  [featureFlags.promptedAssist]: false,
  [featureFlags.radiusPushMfa]: false,
  [featureFlags.readOnlyEmailTemplate]: false,
  [featureFlags.remoteAssist]: true,
  [featureFlags.remoteAssistForLinux]: true,
  [featureFlags.remoteAssistOnboarding]: true,
  [featureFlags.reportFiltration]: false,
  [featureFlags.samlMultiAcs]: false,
  [featureFlags.scimSupportGong]: true,
  [featureFlags.scimSupportPaylocity]: true,
  [featureFlags.settingsFeatureTrials]: true,
  [featureFlags.ssoCustomSamlSubjectNameId]: false,
  [featureFlags.ssoHiddenApps]: false,
  [featureFlags.ssoReport]: false,
  [featureFlags.stagedUserEnhancements]: false,
  [featureFlags.synthesizedReports]: false,
  [featureFlags.systemInsightsLocalDeviceAccounts]: false,
  [featureFlags.trialRecaps]: false,
  [featureFlags.upgradeNudge]: false,
  [featureFlags.upgradePage]: false,
  [featureFlags.usePaginationMethodForBoundDevices]: false,
  [featureFlags.usersToDevicesReport]: true,
  [featureFlags.usersToDirectoriesReport]: true,
  [featureFlags.usersToLdapServersReport]: true,
  [featureFlags.usersToRadiusServersReport]: true,
  [featureFlags.valueReport]: false,
  [featureFlags.windowsMdmEnrollment]: false,
  [featureFlags.zzzFlagForTesting]: true,
};

/**
 * The Feature Flags Module is meant to replace using the SettingsModule when querying
 * feature flags. Currently it gets feature flags from Mongo via the SettingsModule, and then
 * also queries LaunchDarkly for feature flags. LaunchDarkly will be replacing Mongo for the feature
 * flag store in the near term. But both sources are currently supported.
 *
 * The Feature Flags Module _will_ override Mongo flags with LaunchDarkly flags when a record
 * exists in the featureFlagToProviderMap. For example, a "featureName" flag from
 * Mongo is used to toggle user experience.  However, a record exists in the
 * featureFlagToProviderMap, mapping a LaunchDarkly feature flag to
 * the name of a feature flag found in Mongo, so the value from LaunchDarkly will _replace_ the
 * value from Mongo.
 *
 * This functionality has two purposes:
 *
 * 1. It allows us to replace existing Mongo feature flags with LaunchDarkly feature flags.
 * 2. It allows us to use more succinct names for feature flags in client side code, e.g.
 *    "featureName" instead of "YYYY-MM-DD-feature-name".
 */
export function createFeatureFlagsModule(config = {}) {
  const {
    LaunchDarklyClient = LDClient,
    configService = ConfigService,
    localStorageService = LocalStorageService,
    pageNavigationUtil = PageNavigationUtil,
  } = config;

  return {
    namespaced: true,

    state: {
      betaFeatures: {},
    },

    actions: {
      /**
       * Important: this requires SettingsModule/fetchUserSettings and
       * SettingsModule/fetchSettings to happen first in order to get the userId and orgId.
       */
      async fetchFeatureFlags({ dispatch }) {
        dispatch('getBetaFeatures');

        if (configService.launchDarklySource === 'localStorage') {
          dispatch('initLocalStorageClient');
        } else {
          await dispatch('initLaunchDarklyClient');
        }
      },

      /**
       * @todo Consider calling SettingsApi.getGlobalSettings() to fetch our own copy of the beta
       * features _and_ not make this module depend on the SettingsModule to get the beta
       * features from Mongo.
       */
      getBetaFeatures({ commit, rootState }) {
        const { betaFeatures } = rootState[settingsModule];

        if (betaFeatures) {
          Object.entries(betaFeatures).forEach(([key, value]) => {
            commit('UPDATE_BETA_FEATURES', { [key]: value });
          });

          // SI also rerturns feature flags from LaunchDarkly so those values are mapped here
          // first. The LaunchDarkly client will only push _changes_ to feature flags to us so the
          // initial values from SI should be mapped, and honored, first. If these feature flags
          // change in the current session then the change handler registered below in the
          // initLaunchDarklyClient action will be called to update them.

          Object.entries(featureFlagToProviderMap).forEach(([featureFlag, ldFlagName]) => {
            if (betaFeatures[ldFlagName] !== undefined) {
              commit('UPDATE_BETA_FEATURES', { [featureFlag]: betaFeatures[ldFlagName] });
            }
          });
        }
      },

      async initLaunchDarklyClient({ commit, rootState }) {
        let ldClient;

        const processLaunchDarklySettings = (settings) => {
          Object.keys(featureFlagToProviderMap).forEach((key) => {
            const flag = featureFlagToProviderMap[key];

            const value = settings
              ? settings[flag] && settings[flag].current
              : ldClient.variation(flag, undefined);

            if (value !== undefined) {
              commit('UPDATE_BETA_FEATURES', { [key]: value });
            }
          });
        };

        return new Promise((resolve) => {
          const { launchDarklyClientSideId, launchDarklyBaseUrls } = configService;
          const { name, orgId, user } = rootState[settingsModule];

          const id = user && user.provider ? user.provider : orgId;
          const key = shajs('sha256').update(String(id)).digest('hex');

          if (!id) {
            resolve();
            return;
          }

          const payload = { key };
          // Only pass the cleartext name if it exists
          if (name) {
            payload.name = name;
          }

          const options = {
            streaming: process.env.NODE_ENV !== 'development',
          };
          if (!isEmpty(launchDarklyBaseUrls)) {
            Object.entries(launchDarklyBaseUrls).forEach(([optionKey, optionValue]) => {
              // Only set the config values if an explicit value was given,
              // so that the default value is controlled by the LD SDK and not us.
              if (optionValue !== undefined && optionValue !== null) {
                options[optionKey] = optionValue;
              }
            });
          }

          ldClient = LaunchDarklyClient.initialize(launchDarklyClientSideId, payload, options);

          ldClient.on('ready', () => {
            processLaunchDarklySettings();
            ldClient.on('change', settings => processLaunchDarklySettings(settings));
            resolve();
          });
        });
      },

      /**
       * When the launchDarklySource field of the GET /ui-settings.json response is set to
       * localStorage then feature flags will be read from local storage.
       *
       * Follow the steps below to use local storage as the feature flag source.
       *
       * 1. Edit /env/ui-settings.json in jumpcloud-workstation
       *
       *    diff --git a/env/ui-settings.json b/env/ui-settings.json
       *    index df424e6..5a4ad50 100644
       *    --- a/env/ui-settings.json
       *    +++ b/env/ui-settings.json
       *    @@ -2,6 +2,7 @@
       *       "deviceCertAuthCodeUrl": "https://device-cert.jumpcloud.local/auth/code",
       *       "driftScriptUrl": "https://thirdparty.jumpcloud.com/js/drift-stg.min.js",
       *       "launchDarklyClientSideId": "5eb98a3db092960a8897c0f9",
       *    +  "launchDarklySource": "localStorage",
       *       "segmentScriptUrl": "https://thirdparty.jumpcloud.com/js/segment-local.min.js",
       *       "signupFbConfigUrl": "https://thirdparty.jumpcloud.com/js/fb-config-stg.min.js",
       *       "signupFbEventsUrl": "https://thirdparty.jumpcloud.com/js/fb-events-stg.min.js",
       *
       * 2. Stop and start all workstation services.
       *
       * 3. Navigate to https://console.jumpcloud.local
       *
       * 4. Write feature flags to local storage:
       *
       *    window.localStorage.setItem('2021-03-19-use-new-mtp-repo', true);
       *    window.localStorage.setItem('2021-03-24-mtp-ui-refresh', true);
       */
      initLocalStorageClient({ commit }) {
        const setFeatureFlags = () => {
          try {
            Object.entries(featureFlagToProviderMap).forEach(([key, flag]) => {
              let value = localStorageService.getValue(flag);

              // If the feature flag was not found in local storage then check for a default value.
              // Default values are provided when working with local storage as the feature flag
              // source so that flags that have targeting enabled and serve a default value in LD
              // have the same functionality when working with local storage.

              if (value === null) {
                value = featureFlagDefaultValues[key];
              }

              if (typeof value === 'boolean') {
                commit('UPDATE_BETA_FEATURES', { [key]: value });
              }
            });
          } catch (exp) {
            // eslint-disable-next-line
            console.error(`FeatureFlagsModule.initLocalStorageClient(): Error reading feature flags from localStorage = ${exp.toString()}`);
          }
        };

        setFeatureFlags();

        // NOTE: The storage event _must_ be triggered in another document (i.e. a new tab). The
        // event will not fire when the change to localStorage is initiated in the document where
        // the event listener was added.
        // SEE: https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
        pageNavigationUtil.addEventListener(EventConsts.storage, setFeatureFlags);
      },

      resetFeatureFlags({ commit }) {
        commit('RESET_FEATURE_FLAGS');
      },
    },

    getters: {
      betaFeatures(state) {
        return state.betaFeatures;
      },
    },

    mutations: {
      UPDATE_BETA_FEATURES(state, payload) {
        Object.keys(payload).forEach((key) => {
          Vue.set(state.betaFeatures, key, payload[key]);
        });
      },

      RESET_FEATURE_FLAGS(state) {
        Vue.set(state, 'betaFeatures', {});
      },
    },
  };
}

export default createFeatureFlagsModule();
